<template>
  <teleport to="body">
    <transition name="modal-fade">
      <div v-if="showModal" class="modal-overlay" v-on:click.self="closeModal">
        <transition name="slide-in">
          <div v-if="showModal" class="modal-panel" :class="{ 'is-scanner': isScanning }">
            <!-- The live scanner is a full-screen camera with its own overlaid
                 back button, so the standard header is hidden while scanning. -->
            <header v-if="!isScanning" class="modal-head">
              <div>
                <div class="modal-eyebrow">
                  <span class="status-dot"></span>
                  Scan receipt
                </div>
                <h2>Scan a receipt</h2>
                <p>Point your camera at the bank receipt — we read the transaction number for you.</p>
              </div>
              <button type="button" class="icon-button" v-on:click="closeModal" aria-label="Close">
                <font-awesome-icon icon="fa-solid fa-xmark" />
              </button>
            </header>

            <!-- DESKTOP: scanning needs a phone camera -->
            <div v-if="!canScan && !result && !showManual" class="modal-form">
              <div class="modal-body">
                <section class="desktop-section">
                  <div class="desktop-icon"><i class="pi pi-mobile"></i></div>
                  <h3>Scan from your phone</h3>
                  <p>
                    Receipt scanning uses your phone's camera. Open abolverify.com in your
                    mobile browser to scan a receipt, or download the app from PlayStore.
                  </p>
                  <a class="primary-button desktop-store" :href="playStoreUrl" target="_blank" rel="noopener">
                    <i class="pi pi-google"></i>
                    <span>Get it on Google Play</span>
                  </a>
                </section>
              </div>
              <footer class="modal-foot">
                <button type="button" class="ghost-button" v-on:click="closeModal">Close</button>
                <button type="button" class="primary-button" v-on:click="enterManual">
                  <i class="pi pi-pencil"></i>
                  <span>Enter ID manually</span>
                </button>
              </footer>
            </div>

            <!-- RESULT -->
            <div v-if="result" class="modal-form">
              <div class="modal-body">
                <section class="result-section" :class="result.alreadyVerified ? 'is-info' : 'is-success'">
                  <div class="result-icon"><i class="pi pi-check"></i></div>
                  <h3>{{ result.alreadyVerified ? "Already verified" : "Transaction verified" }}</h3>
                  <p v-if="result.alreadyVerified">This transaction was verified earlier — here are its details.</p>
                  <p v-else>The payment was confirmed against the bank receipt.</p>

                  <dl class="result-grid">
                    <div class="result-row">
                      <dt>Amount</dt>
                      <dd class="result-amount">{{ amount(result) }}</dd>
                    </div>
                    <div class="result-row">
                      <dt>Bank</dt>
                      <dd>{{ bankLabel(result.bank) }}</dd>
                    </div>
                    <div class="result-row">
                      <dt>Reference</dt>
                      <dd class="mono">{{ result.transactionReference || result.transactionId || "—" }}</dd>
                    </div>
                    <div class="result-row">
                      <dt>Payer</dt>
                      <dd>{{ result.accountName || "—" }}</dd>
                    </div>
                    <div v-if="result.accountNumber" class="result-row">
                      <dt>Payer account</dt>
                      <dd class="mono">{{ result.accountNumber }}</dd>
                    </div>
                    <div class="result-row">
                      <dt>Date</dt>
                      <dd>{{ $filters.getDateAndTime(result.date) }}</dd>
                    </div>
                  </dl>
                </section>
              </div>
              <footer class="modal-foot">
                <button type="button" class="ghost-button" v-on:click="scanAnother">{{ canScan ? "Scan another" : "Verify another" }}</button>
                <button type="button" class="primary-button" v-on:click="closeModal">Done</button>
              </footer>
            </div>

            <!-- MANUAL ENTRY -->
            <form v-if="showManual && !result" class="modal-form" v-on:submit.prevent="verifyManual">
              <div class="modal-body">
                <section class="form-section">
                  <h3>Transaction Details</h3>
                  <div class="form-grid">
                    <label class="field full">
                      <span>Bank</span>
                      <select v-model="bank">
                        <option v-for="b in banks" :key="b.value" :value="b.value">{{ b.label }}</option>
                      </select>
                    </label>

                    <label v-if="accountRequired" class="field full">
                      <span>Bank Account <small>(required)</small></span>
                      <select v-model="recipientAccountId">
                        <option :value="null">Select a bank account</option>
                        <option v-for="account in filteredAccounts" :key="account.id" :value="account.id">
                          {{ accountLabel(account) }}
                        </option>
                      </select>
                      <small v-if="accountRequired && !bankAccountsLoading && filteredAccounts.length === 0" class="hint">
                        No {{ bankLabel(bank) }} accounts yet.
                        <a class="link" v-on:click="goToBankAccounts">Add one in Bank Accounts.</a>
                      </small>
                    </label>

                    <label class="field full">
                      <span>Reference ID</span>
                      <input
                        v-model.trim="manualId"
                        type="text"
                        inputmode="text"
                        placeholder="e.g. FT25ABC12345"
                        autocomplete="off"
                        v-on:input="onManualInput"
                      />
                      <span v-if="manualError" class="form-text text-danger">{{ manualError }}</span>
                      <small class="hint">The transaction / receipt reference from the payment, letters and numbers only.</small>
                    </label>
                  </div>
                </section>
              </div>
              <footer class="modal-foot">
                <button type="button" class="ghost-button" v-on:click="backToScanner">{{ canScan ? "Back to scan" : "Back" }}</button>
                <button type="submit" class="primary-button" :disabled="busy">
                  <span v-if="busy" class="btn-spinner" aria-hidden="true"></span>
                  <span>{{ busy ? "Verifying…" : "Verify" }}</span>
                </button>
              </footer>
            </form>

            <!-- SCANNER — full-screen camera with overlaid controls -->
            <div v-if="isScanning" class="scanner">
              <video ref="video" class="scan-video" playsinline autoplay muted></video>

              <!-- Top overlay: back button + bank / account controls -->
              <div class="scan-topbar">
                <button type="button" class="scan-back" v-on:click="closeModal" aria-label="Go back">
                  <font-awesome-icon icon="fa-solid fa-arrow-left" />
                </button>
                <div class="scan-controls">
                  <div class="bank-pills" role="tablist" aria-label="Bank">
                    <button
                      v-for="b in banks"
                      :key="b.value"
                      type="button"
                      role="tab"
                      :class="['bank-pill', { 'is-active': bank === b.value }]"
                      v-on:click="selectBank(b.value)"
                    >
                      {{ b.label }}
                    </button>
                  </div>
                  <select v-if="accountRequired" v-model="recipientAccountId" class="account-select" aria-label="Bank account">
                    <option :value="null">{{ filteredAccounts.length ? "Select bank account" : "No accounts" }}</option>
                    <option v-for="account in filteredAccounts" :key="account.id" :value="account.id">
                      {{ accountLabel(account) }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- Camera error / permission -->
              <div v-if="cameraError" class="scan-cover scan-error">
                <i class="pi pi-video"></i>
                <p>{{ cameraError }}</p>
                <button type="button" class="ghost-button on-dark" v-on:click="setupCamera">
                  <i class="pi pi-refresh"></i>
                  <span>Try again</span>
                </button>
              </div>

              <!-- Warming up the camera -->
              <div v-else-if="!cameraReady" class="scan-cover">
                <span class="scan-spinner" aria-hidden="true"></span>
                <p>Starting camera…</p>
              </div>

              <!-- Framing guide -->
              <div v-else-if="!busy" class="scan-frame" aria-hidden="true">
                <span class="corner tl"></span>
                <span class="corner tr"></span>
                <span class="corner bl"></span>
                <span class="corner br"></span>
                <span class="scan-line"></span>
              </div>

              <!-- Reading / verifying -->
              <div v-if="busy" class="scan-cover scan-busy">
                <span class="scan-spinner" aria-hidden="true"></span>
                <p>{{ statusText }}</p>
                <small>You can lower your phone — we've got it.</small>
              </div>

              <!-- Hint -->
              <div v-if="cameraReady && !busy" class="scan-hint">
                {{ accountRequired && !recipientAccountId
                  ? "Select the bank account first"
                  : "Fit the " + bankLabel(bank) + " transaction number in the frame" }}
              </div>

              <!-- Bottom overlay: capture actions -->
              <footer class="scan-actions">
                <button type="button" class="round-btn" v-on:click="triggerFilePick" :disabled="busy" aria-label="Upload a receipt photo">
                  <i class="pi pi-image"></i>
                </button>
                <button
                  type="button"
                  class="shutter"
                  :disabled="!cameraReady || busy"
                  v-on:click="captureAndVerify"
                  aria-label="Scan receipt"
                >
                  <i class="pi pi-camera"></i>
                </button>
                <button type="button" class="round-btn" v-on:click="enterManual" :disabled="busy" aria-label="Enter ID manually">
                  <i class="pi pi-pencil"></i>
                </button>
              </footer>

              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                class="visually-hidden"
                v-on:change="onFilePicked"
              />
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script>
import VerificationService from "@/services/verification";
import { GET_BANK_ACCOUNTS, VERIFY_TRANSACTION } from "@/store/action_types";
import { BANKS, BANK_LABELS } from "@/utils/enums";
import { PLAY_STORE_URL, TOAST_GLOBAL_CONFIG } from "@/utils/const";
import { BANK_ACCOUNTS_ROUTE } from "@/router/routes";
import { canScanReceipt } from "@/utils/device";
import { extractTransactionId } from "@/utils/transaction_id_extractor";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faXmark, faArrowLeft } from "@fortawesome/free-solid-svg-icons";

library.add(faXmark, faArrowLeft);

export default {
  emits: ["close-scan-modal", "verified"],
  data() {
    return {
      showModal: true,
      canScan: canScanReceipt(),
      playStoreUrl: PLAY_STORE_URL,
      // The /verify endpoint supports CBE and telebirr.
      banks: [
        { label: BANK_LABELS[BANKS.cbe], value: BANKS.cbe },
        { label: BANK_LABELS[BANKS.telebirr], value: BANKS.telebirr },
      ],
      bank: BANKS.cbe,
      recipientAccountId: null,
      cameraReady: false,
      cameraError: "",
      busy: false,
      statusText: "",
      result: null,
      showManual: false,
      manualId: "",
      manualError: "",
    };
  },
  computed: {
    bankAccounts() {
      return this.$store.getters.bankAccounts.data || [];
    },
    bankAccountsLoading() {
      return this.$store.getters.bankAccounts.isLoading;
    },
    // Only active accounts matching the chosen bank can be used (the backend rejects a mismatch).
    filteredAccounts() {
      return this.bankAccounts.filter(
        (a) => a.bank === this.bank && a.isActive && !a.isDeleted,
      );
    },
    // CBE receipts are resolved using the recipient account number.
    accountRequired() {
      return this.bank === BANKS.cbe;
    },
    // The live camera scanner (mobile only) renders full-screen with overlaid
    // controls; every other state uses the normal header + panel layout.
    isScanning() {
      return this.canScan && !this.result && !this.showManual;
    },
  },
  watch: {
    bank() {
      // A stale account from a different bank must not carry over.
      this.recipientAccountId = null;
    },
  },
  methods: {
    bankLabel(bank) {
      return BANK_LABELS[bank] || String(bank || "").toUpperCase();
    },
    accountLabel(account) {
      const name = account.name || account.accountName || "Account";
      return `${name} · ${account.accountNumber}`;
    },
    amount(record) {
      const value = Number(record.amount);
      if (isNaN(value)) return "—";
      return this.$filters.formatPrice(value, record.currency || null);
    },
    selectBank(bank) {
      this.bank = bank;
    },

    // ---- Camera -----------------------------------------------------------
    async setupCamera() {
      this.cameraError = "";
      this.cameraReady = false;
      try {
        this.stopStream();
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        const video = this.$refs.video;
        if (!video) return; // modal closed mid-setup
        video.srcObject = this.stream;
        await video.play().catch(() => {});
        this.cameraReady = true;
      } catch (e) {
        const name = e && e.name;
        if (name === "NotAllowedError" || name === "SecurityError") {
          this.cameraError =
            "Camera access is blocked. Allow camera for this site in your browser settings, then tap Try again.";
        } else if (name === "NotFoundError" || name === "OverconstrainedError") {
          this.cameraError = "No camera available on this device.";
        } else {
          this.cameraError = "Could not start the camera. Please try again.";
        }
      }
    },
    stopStream() {
      if (this.stream) {
        for (const track of this.stream.getTracks()) track.stop();
        this.stream = null;
      }
    },

    // ---- OCR worker (lazy, reused) ----------------------------------------
    ensureWorker() {
      if (!this.workerPromise) {
        this.workerPromise = (async () => {
          const { createWorker } = await import("tesseract.js");
          const worker = await createWorker("eng");
          // The modal may have been closed while the engine was loading — don't
          // leak a worker that nothing will ever terminate.
          if (this.destroyed) {
            try {
              worker.terminate();
            } catch (e) {
              /* already gone */
            }
            throw new Error("scanner closed");
          }
          this.worker = worker;
          return worker;
        })();
      }
      return this.workerPromise;
    },
    async runOcr(imageLike) {
      const worker = await this.ensureWorker();
      const { data } = await worker.recognize(imageLike);
      return (data && data.text) || "";
    },

    // ---- Capture / pick → OCR → verify ------------------------------------
    async captureAndVerify() {
      if (this.busy || !this.cameraReady) return;
      if (this.accountRequired && !this.recipientAccountId) {
        this.$toast.error("Select a bank account first.", TOAST_GLOBAL_CONFIG);
        return;
      }
      const video = this.$refs.video;
      if (!video || !video.videoWidth) return;

      this.busy = true;
      this.statusText = "Reading receipt…";
      try {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);

        const text = await this.runOcr(canvas);
        const transactionId = extractTransactionId(this.bank, text);
        if (!transactionId) {
          this.onScanFailed();
          return;
        }
        const blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, "image/jpeg", 0.9),
        );
        await this.verify(transactionId, blob);
      } catch (e) {
        this.busy = false;
        this.statusText = "";
        this.$toast.error("Could not scan the receipt. Try again.", TOAST_GLOBAL_CONFIG);
      }
    },
    triggerFilePick() {
      if (this.busy) return;
      if (this.accountRequired && !this.recipientAccountId) {
        this.$toast.error("Select a bank account first.", TOAST_GLOBAL_CONFIG);
        return;
      }
      this.$refs.fileInput && this.$refs.fileInput.click();
    },
    async onFilePicked(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = ""; // allow re-picking the same file
      if (!file) return;

      this.busy = true;
      this.statusText = "Reading receipt…";
      try {
        const text = await this.runOcr(file);
        const transactionId = extractTransactionId(this.bank, text);
        if (!transactionId) {
          this.onScanFailed();
          return;
        }
        await this.verify(transactionId, file);
      } catch (e) {
        this.busy = false;
        this.statusText = "";
        this.$toast.error("Could not read that image. Try another.", TOAST_GLOBAL_CONFIG);
      }
    },
    onScanFailed() {
      this.busy = false;
      this.statusText = "";
      this.manualError = "";
      this.manualId = "";
      this.showManual = true;
      this.$toast.warning(
        "Couldn't read the transaction number — enter it manually or scan again.",
        TOAST_GLOBAL_CONFIG,
      );
    },

    // ---- Verify -----------------------------------------------------------
    async verify(transactionId, blob) {
      this.busy = true;
      this.statusText = "Verifying…";
      const payload = {
        bank: this.bank,
        transactionId: String(transactionId).trim(),
      };
      if (this.recipientAccountId) payload.recipientAccountId = this.recipientAccountId;

      const res = await this.$store.dispatch(VERIFY_TRANSACTION, payload);
      this.busy = false;
      this.statusText = "";

      if (!res) {
        // A scan-initiated verify that failed (most often the transaction id was
        // misread by OCR, so the bank has no such receipt) is recoverable: drop the
        // user into manual entry with the scanned id pre-filled, so they can fix a
        // character instead of retyping from scratch. A toast alone would strand
        // them. Manual attempts (blob === null) stay where they are.
        if (blob) {
          this.manualId = String(transactionId).trim().toUpperCase();
          this.showManual = true;
        }
        this.$toast.error(
          this.$store.getters.errorMsg || "Verification failed. Please try again.",
          TOAST_GLOBAL_CONFIG,
        );
        return;
      }

      // Attach the captured receipt image in the background — fire-and-forget so
      // it adds no latency. Skipped for an already-verified transaction (it keeps
      // its original receipt). Best-effort: failures never surface.
      if (blob && res.id && !res.alreadyVerified) {
        VerificationService.attachReceiptImage(res.id, blob).catch(() => {});
      }

      this.result = res;
      this.showManual = false;
      this.$emit("verified");
    },
    verifyManual() {
      if (this.busy) return;
      const ref = (this.manualId || "").trim();
      if (!ref) {
        this.manualError = "Reference ID is required";
        return;
      }
      if (!/^[A-Za-z0-9]+$/.test(ref)) {
        this.manualError = "Reference ID may only contain letters and numbers";
        return;
      }
      if (this.accountRequired && !this.recipientAccountId) {
        this.manualError = "";
        this.$toast.error("Select a bank account first.", TOAST_GLOBAL_CONFIG);
        return;
      }
      this.manualError = "";
      this.verify(ref.toUpperCase(), null);
    },
    onManualInput() {
      if (this.manualError) this.manualError = "";
    },

    // ---- Navigation between states ----------------------------------------
    enterManual() {
      this.manualError = "";
      this.showManual = true;
    },
    backToScanner() {
      this.showManual = false;
      this.manualId = "";
      this.manualError = "";
      if (this.canScan && !this.cameraReady) this.setupCamera();
    },
    scanAnother() {
      this.result = null;
      this.manualId = "";
      if (this.canScan) {
        this.showManual = false;
        if (!this.cameraReady) this.setupCamera();
      } else {
        // Desktop has no camera — go straight back to manual entry.
        this.showManual = true;
      }
    },
    goToBankAccounts() {
      this.closeModal();
      this.$router.push({ name: BANK_ACCOUNTS_ROUTE });
    },
    closeModal() {
      // Release the camera straight away so the device's camera light goes off
      // immediately, rather than waiting for the slide-out transition to unmount.
      this.stopStream();
      this.cameraReady = false;
      this.$emit("close-scan-modal");
    },
  },
  created() {
    // Non-reactive handles (kept off data() so Vue doesn't proxy them).
    this.stream = null;
    this.worker = null;
    this.workerPromise = null;
    this.destroyed = false;
  },
  mounted() {
    if (this.canScan) {
      // Load the recipient accounts (for CBE), warm the OCR engine, and start
      // the camera — all in parallel so the first capture is fast.
      this.$store.dispatch(GET_BANK_ACCOUNTS, { isActive: true, limit: 100 });
      this.ensureWorker().catch(() => {});
      this.setupCamera();
    } else {
      // Desktop still needs the accounts for the manual fallback.
      this.$store.dispatch(GET_BANK_ACCOUNTS, { isActive: true, limit: 100 });
    }
  },
  beforeUnmount() {
    this.destroyed = true;
    this.stopStream();
    if (this.worker) {
      try {
        this.worker.terminate();
      } catch (e) {
        /* worker may already be gone */
      }
      this.worker = null;
    }
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
}

.modal-panel {
  --dashboard-blue: #73c41d;
  --dashboard-blue-dark: #538c13;
  --dashboard-blue-soft: #eef6df;
  --dashboard-blue-border: #cfe6a3;
  --dashboard-slate: #34404a;
  --dashboard-muted: #76828b;
  --dashboard-border: #e3e8e6;
  --dashboard-canvas: #f4f6f3;
  --dashboard-green: #4aa12a;
  --dashboard-red: #e0464d;
  width: 480px;
  max-width: 95vw;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-left: 1px solid var(--dashboard-border);
  box-shadow: -24px 0 60px rgba(15, 23, 42, 0.18);
  color: var(--dashboard-slate);
}

/* The live camera scanner always fills the whole screen — even on tablet/desktop
   widths where the panel is otherwise a right-hand side sheet. */
.modal-panel.is-scanner {
  width: 100vw;
  max-width: 100vw;
}

.modal-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid var(--dashboard-border);
}

.modal-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 5px 11px;
  margin-bottom: 8px;
  border: 1px solid var(--dashboard-border);
  border-radius: var(--ehs-radius-pill);
  background: var(--dashboard-blue-soft);
  color: var(--dashboard-blue-dark);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--dashboard-green);
  box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.12);
}

.modal-head h2 {
  margin: 0;
  font-family: var(--ehs-font-display);
  font-size: 1.4rem;
  font-weight: 600;
  letter-spacing: -0.025em;
}

.modal-head p {
  margin: 4px 0 0;
  color: var(--dashboard-muted);
  font-size: 0.86rem;
}

.icon-button {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--dashboard-border);
  background: #ffffff;
  border-radius: 50%;
  color: var(--dashboard-slate);
  cursor: pointer;
  box-shadow: none !important;
  flex-shrink: 0;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}

.icon-button:hover {
  background: var(--dashboard-blue-soft);
  color: var(--dashboard-blue-dark);
  border-color: var(--dashboard-blue-border);
}

.modal-form {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 22px 24px 32px;
  background: var(--dashboard-canvas);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-section {
  padding: 18px 20px;
  background: #ffffff;
  border: 1px solid var(--dashboard-border);
  border-radius: 16px;
}

.form-section h3 {
  margin: 0 0 14px;
  font-family: var(--ehs-font-display);
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field > span {
  color: var(--dashboard-muted);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.field > span small {
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
}

.field input,
.field select {
  padding: 10px 12px;
  border: 1px solid var(--dashboard-border);
  border-radius: 10px;
  background: #ffffff;
  color: var(--dashboard-slate);
  font-size: 0.86rem;
  font-family: inherit;
  outline: none;
  width: 100%;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.field input:focus,
.field select:focus {
  border-color: var(--dashboard-blue);
  box-shadow: 0 0 0 3px rgba(115, 196, 29, 0.18);
}

.form-text.text-danger {
  margin-top: 4px;
  color: var(--dashboard-red);
  font-size: 0.74rem;
  font-weight: 500;
  position: relative;
  left: 4px;
}

.hint {
  margin-top: 4px;
  color: var(--dashboard-muted);
  font-size: 0.74rem;
  line-height: 1.5;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 400;
}

.hint .link {
  color: var(--dashboard-blue-dark);
  font-weight: 600;
  cursor: pointer;
}

.hint .link:hover {
  text-decoration: underline;
}

/* Desktop "scan from your phone" */
.desktop-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  padding: 30px 22px;
  background: #ffffff;
  border: 1px solid var(--dashboard-border);
  border-radius: 16px;
}

.desktop-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 4px;
  border-radius: 50%;
  background: var(--dashboard-blue-soft);
  color: var(--dashboard-blue-dark);
  font-size: 1.4rem;
}

.desktop-section h3 {
  margin: 0;
  font-family: var(--ehs-font-display);
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--dashboard-slate);
}

.desktop-section p {
  margin: 0;
  max-width: 340px;
  color: var(--dashboard-muted);
  font-size: 0.86rem;
  line-height: 1.55;
}

.desktop-store {
  margin-top: 10px;
  text-decoration: none;
}

/* Result */
.result-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 26px 22px;
  background: #ffffff;
  border: 1px solid var(--dashboard-border);
  border-radius: 16px;
}

.result-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 12px;
  border-radius: 50%;
  font-size: 1.4rem;
}

.result-section.is-success .result-icon {
  background: var(--ehs-color-success-soft);
  color: var(--dashboard-green);
}

.result-section.is-info .result-icon {
  background: var(--dashboard-blue-soft);
  color: var(--dashboard-blue-dark);
}

.result-section h3 {
  margin: 0;
  font-family: var(--ehs-font-display);
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--dashboard-slate);
}

.result-section > p {
  margin: 6px 0 0;
  color: var(--dashboard-muted);
  font-size: 0.84rem;
}

.result-grid {
  width: 100%;
  margin: 18px 0 0;
  display: grid;
  gap: 0;
  text-align: left;
  border-top: 1px solid var(--dashboard-border);
}

.result-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  padding: 11px 2px;
  border-bottom: 1px solid var(--dashboard-border);
}

.result-row dt {
  margin: 0;
  color: var(--dashboard-muted);
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.result-row dd {
  margin: 0;
  color: var(--dashboard-slate);
  font-size: 0.88rem;
  font-weight: 500;
  text-align: right;
  word-break: break-word;
}

.result-row dd.mono {
  font-family: var(--ehs-font-mono);
  font-size: 0.82rem;
}

.result-row dd.result-amount {
  font-family: var(--ehs-font-display);
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--dashboard-blue-dark);
}

/* Footer buttons */
.modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--dashboard-border);
  background: #ffffff;
}

.ghost-button,
.primary-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: var(--ehs-radius-pill);
  border: 1px solid transparent;
  padding: 10px 20px;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: none !important;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

.ghost-button {
  background: #ffffff;
  color: var(--dashboard-slate);
  border-color: var(--dashboard-border);
}

.ghost-button:hover {
  background: var(--dashboard-blue-soft);
  color: var(--dashboard-blue-dark);
  border-color: var(--dashboard-blue-border);
}

.primary-button {
  background: var(--dashboard-blue);
  color: #ffffff;
  border-color: var(--dashboard-blue);
}

.primary-button:hover:not(:disabled) {
  background: var(--dashboard-blue-dark);
  border-color: var(--dashboard-blue-dark);
}

.primary-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.primary-button .pi {
  font-size: 0.78rem;
}

.btn-spinner,
.scan-spinner {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #ffffff;
  animation: scan-spin 0.6s linear infinite;
}

@keyframes scan-spin {
  to { transform: rotate(360deg); }
}

/* ---- Scanner (full-screen camera) ---- */
.scanner {
  position: relative;
  flex: 1;
  min-height: 0;
  background: #0b1220;
  overflow: hidden;
}

/* Top overlay: back button + bank/account controls floating over the camera */
.scan-topbar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 4;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 14px 18px;
  background: linear-gradient(to bottom, rgba(11, 18, 32, 0.78) 0%, rgba(11, 18, 32, 0) 100%);
}

.scan-back {
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(11, 18, 32, 0.55);
  border-radius: 50%;
  color: #ffffff;
  font-size: 0.95rem;
  cursor: pointer;
  box-shadow: none !important;
  transition: background 0.18s ease, border-color 0.18s ease;
}

.scan-back:hover {
  background: rgba(11, 18, 32, 0.8);
  border-color: rgba(255, 255, 255, 0.6);
}

.scan-controls {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.bank-pills {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  background: #ffffff;
  border: 1px solid var(--dashboard-border);
  border-radius: var(--ehs-radius-pill);
}

.bank-pill {
  border: 0;
  background: transparent;
  padding: 7px 16px;
  border-radius: var(--ehs-radius-pill);
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--dashboard-muted);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.bank-pill.is-active {
  background: var(--dashboard-blue-soft);
  color: var(--dashboard-blue-dark);
}

.account-select {
  flex: 1 1 auto;
  min-width: 0;
  height: 38px;
  padding: 0 12px;
  border: 1px solid var(--dashboard-border);
  border-radius: 10px;
  background: #ffffff;
  color: var(--dashboard-slate);
  font-size: 0.8rem;
  font-family: inherit;
  outline: none;
}

.account-select:focus {
  border-color: var(--dashboard-blue);
  box-shadow: 0 0 0 3px rgba(115, 196, 29, 0.18);
}

.scan-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.scan-cover {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 28px;
  text-align: center;
  color: #ffffff;
  background: rgba(11, 18, 32, 0.82);
}

.scan-cover .pi {
  font-size: 2rem;
  color: rgba(255, 255, 255, 0.85);
}

.scan-cover p {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  max-width: 320px;
}

.scan-busy small {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.78rem;
}

.scan-spinner {
  width: 34px;
  height: 34px;
  border-width: 3px;
  border-color: rgba(255, 255, 255, 0.35);
  border-top-color: var(--dashboard-blue);
}

.on-dark {
  background: transparent;
  color: #ffffff;
  border-color: rgba(255, 255, 255, 0.5);
}

.on-dark:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  border-color: #ffffff;
}

/* Framing guide */
.scan-frame {
  position: absolute;
  inset: 16% 8% 24%;
  border-radius: 16px;
  box-shadow: 0 0 0 100vmax rgba(11, 18, 32, 0.45);
  overflow: hidden;
}

.corner {
  position: absolute;
  width: 26px;
  height: 26px;
  border: 3px solid var(--dashboard-blue);
}

.corner.tl { top: -1px; left: -1px; border-right: 0; border-bottom: 0; border-top-left-radius: 14px; }
.corner.tr { top: -1px; right: -1px; border-left: 0; border-bottom: 0; border-top-right-radius: 14px; }
.corner.bl { bottom: -1px; left: -1px; border-right: 0; border-top: 0; border-bottom-left-radius: 14px; }
.corner.br { bottom: -1px; right: -1px; border-left: 0; border-top: 0; border-bottom-right-radius: 14px; }

.scan-line {
  position: absolute;
  left: 8px;
  right: 8px;
  height: 2px;
  background: var(--dashboard-blue);
  box-shadow: 0 0 12px 1px rgba(115, 196, 29, 0.8);
  animation: scan-sweep 2.4s ease-in-out infinite alternate;
}

@keyframes scan-sweep {
  from { top: 8px; }
  to { top: calc(100% - 10px); }
}

.scan-hint {
  position: absolute;
  left: 50%;
  bottom: calc(122px + env(safe-area-inset-bottom, 0px));
  transform: translateX(-50%);
  z-index: 4;
  max-width: 86%;
  padding: 7px 14px;
  border-radius: var(--ehs-radius-pill);
  background: rgba(11, 18, 32, 0.6);
  color: #ffffff;
  font-size: 0.78rem;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.scan-actions {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 32px;
  padding: 24px 24px calc(24px + env(safe-area-inset-bottom, 0px));
  background: linear-gradient(to top, rgba(11, 18, 32, 0.82) 0%, rgba(11, 18, 32, 0) 100%);
}

.round-btn {
  width: 52px;
  height: 52px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: rgba(11, 18, 32, 0.5);
  border-radius: 50%;
  color: #ffffff;
  cursor: pointer;
  box-shadow: none !important;
  transition: background 0.18s ease, border-color 0.18s ease;
}

.round-btn:hover:not(:disabled) {
  background: rgba(11, 18, 32, 0.75);
  border-color: rgba(255, 255, 255, 0.7);
}

.round-btn .pi {
  font-size: 1.1rem;
}

.shutter {
  width: 72px;
  height: 72px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 4px solid var(--dashboard-blue-soft);
  background: var(--dashboard-blue);
  border-radius: 50%;
  color: #ffffff;
  cursor: pointer;
  box-shadow: none !important;
  transition: background 0.18s ease, transform 0.1s ease;
}

.shutter:hover:not(:disabled) {
  background: var(--dashboard-blue-dark);
}

.shutter:active:not(:disabled) {
  transform: scale(0.96);
}

.shutter:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.shutter .pi {
  font-size: 1.5rem;
}

.round-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.slide-in-enter-active,
.slide-in-leave-active {
  transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}

.slide-in-enter-from,
.slide-in-leave-to {
  transform: translateX(100%);
}

@media (max-width: 991.98px) {
  .modal-panel {
    width: 100%;
    max-width: 100%;
  }

  .modal-head {
    padding: 16px 16px 14px;
    gap: 10px;
  }

  .modal-head h2 {
    font-size: 1.2rem;
  }

  .modal-head p {
    font-size: 0.8rem;
  }

  .modal-body {
    padding: 16px 14px 24px;
  }

  .form-section {
    padding: 14px 14px;
  }

  .modal-foot {
    padding: 12px 14px;
    flex-direction: column-reverse;
    gap: 8px;
  }

  .modal-foot .ghost-button,
  .modal-foot .primary-button {
    width: 100%;
  }
}
</style>
